import type { DocNode } from '@atlaskit/adf-schema/doc';
import type CardClient from '@atlaskit/link-provider/client';
import type { CardStore } from '@atlaskit/linking-common/store';

import { extractSmartLinksFromAdf } from '@confluence/connectors/entry-points/extractSmartLinksFromAdf.ts';

import { resolveSmartLinkByUrl } from './resolveSmartLinkByUrl.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

type SmartLinkPosition = {
	url: string;
	occurrenceIndex: number;
};

type DraftSmartCard = {
	type?: string;
	attrs?: {
		url?: string;
	};
};

type GlobalWithDraftSmartCards = typeof globalThis & {
	__SMART_CARDS_IN_DRAFT__?: DraftSmartCard[];
};

const getDraftSmartCardsFromWindow = (): DraftSmartCard[] => {
	const draftCards = (globalThis as GlobalWithDraftSmartCards).__SMART_CARDS_IN_DRAFT__;
	return Array.isArray(draftCards) ? draftCards : [];
};

const getDraftSmartLinkPositions = (smartCardsInDraft: DraftSmartCard[]): SmartLinkPosition[] => {
	const seen = new Set<string>();
	const positions: SmartLinkPosition[] = [];
	let counter = 0;

	for (const card of smartCardsInDraft) {
		const type = card?.type;
		const url = card?.attrs?.url;
		const isSmartCardType = type === 'inlineCard' || type === 'blockCard' || type === 'embedCard';
		if (!isSmartCardType || typeof url !== 'string' || url.length === 0 || seen.has(url)) {
			continue;
		}

		seen.add(url);
		positions.push({ url, occurrenceIndex: counter++ });
	}

	return positions;
};

/**
 * Single extract→resolve→map pipeline.
 *
 * 1. Walks the ADF to get ordered { url, occurrenceIndex } positions.
 * 2. Hands each position to `resolveSmartLinkByUrl` (SmartCard store first, then
 *    ORS), which also swallows and reports per-URL failures so one broken link
 *    does not prevent the rest from resolving.
 *
 * Unresolved URLs are kept: the url came from the page's own ADF, so the row
 * belongs in the menu even without metadata.
 */
export async function collectSmartLinks(
	adf: DocNode | null | undefined,
	store: { getState: () => CardStore },
	cardClient: CardClient,
): Promise<EnhancedSmartLink[]> {
	const adfPositions = extractSmartLinksFromAdf(adf, true);
	// The page ADF document is authoritative for the current page once it is available.
	// Only bridge with the draft snapshot (`__SMART_CARDS_IN_DRAFT__`) while the document is
	// not yet available (`adf == null`); otherwise a brand-new/empty page would surface the
	// previous page's stale draft smart cards, since that window global persists across
	// client-side navigation and is not reset when creating a new page.
	const smartCardsInDraft = getDraftSmartCardsFromWindow();
	const draftPositions = getDraftSmartLinkPositions(smartCardsInDraft);
	const positions = adf ? adfPositions : draftPositions;

	if (positions.length === 0) {
		return [];
	}

	return Promise.all(
		positions.map(async ({ url, occurrenceIndex }) => {
			const { link } = await resolveSmartLinkByUrl(url, occurrenceIndex, store, cardClient);
			return link;
		}),
	);
}
